// WARP TO NEXT's and GO's Auto-warp's figures and rules, apart from `drive.ts` so
// the Node harness that flies a skip (`tools/preview/transit-check.mjs --skip`) imports them.

import { DEPARTURE_TIMING_REACH_S } from '../sim/autopilot.ts';
import { WARP_RAMP_NEPERS_PER_S } from '../world/clock.ts';

/** Simulated seconds short of the step the skip stops at. The last day is flown
 *  at the warp levels, so the burn is still something the player watches come. */
export const WARP_SKIP_LEAD_S = 86_400;
/** The lead a second press stops at, once the first has left a day. The wait
 *  ladder floors at 1 MIN/S inside 8 simulated hours, so that day is 8 real
 *  minutes of sitting; ten simulated minutes at 1 MIN/S is ten real seconds. */
export const WARP_SKIP_NEAR_LEAD_S = 600;
/** Simulated seconds one skip tick flies at the coarse substep. */
export const WARP_SKIP_TICK_S = 86_400;
/** The most simulated seconds one skip substep takes, against the 2 s a flown frame
 *  gets. At 120 s a 400 km orbit slips 34 deg a day round itself. */
export const WARP_SKIP_SUBSTEP_S = 120;

/** Simulated seconds short of a step `nextS` away that a press stops at. Never
 *  inside the nose turn the autopilot opens `turnLeadS` ahead of the step, or the
 *  turn and the burn behind it open late. */
export function warpSkipPressLeadS(nextS: number, turnLeadS: number): number {
  const lead = nextS > 2 * WARP_SKIP_LEAD_S ? WARP_SKIP_LEAD_S : WARP_SKIP_NEAR_LEAD_S;
  return Math.max(lead, turnLeadS);
}

/** Whether the next tick may be sized for the coarse substep, off the last command:
 *  an escape's start still untimed, and not about to be timed at that tick's start. */
export function warpSkipTickCoarse(
  departureUntimed: boolean, nextS: number, turnLeadS: number, timingReachS?: number,
): boolean {
  return departureUntimed && nextS - turnLeadS > Math.max(timingReachS ?? 0, DEPARTURE_TIMING_REACH_S);
}

/** Simulated seconds one tick flies at `substepS`: a coarse tick's substeps however
 *  fine they are. */
export function warpSkipTickS(substepS: number): number {
  return (WARP_SKIP_TICK_S * substepS) / WARP_SKIP_SUBSTEP_S;
}

/** Real seconds Auto-warp keeps between the clock and the arrival, or a course fix
 *  whose solve has not landed yet: the rate is the count over this, so the step
 *  comes on at one steady zoom and the solve gets the frames it runs in. */
export const AUTO_WARP_LEAD_REAL_S = 1;
/** Simulated seconds short of a course fix whose solve has not landed that the
 *  approach aims at, crossed at GO's own ladder rate: 20 s at 1 MIN/S for the solve to land in. */
export const AUTO_WARP_FIX_HOLD_S = 1200;
/** The most nepers a real second Auto-warp's rate climbs at, gentler than the
 *  clock's own ramp: a climb is the one change the steady zoom does not pace. */
export const AUTO_WARP_CLIMB_NEPERS_PER_S = 4.5;
/** Real seconds a climb closes its remaining log-distance to the target over, so
 *  it settles onto the steady zoom instead of stopping on it in one frame. */
export const AUTO_WARP_SETTLE_S = 1;
/** The fewest real seconds a step may sit away at Auto-warp's rate while that
 *  rate is still easing down onto the count. */
export const AUTO_WARP_MIN_LEAD_REAL_S = 0.25;
/** The lead every other step gets: the departure window, and a course fix already
 *  solved. Twice the floor above, so the ease follows the count down and never meets it. */
export const AUTO_WARP_SHORT_LEAD_REAL_S = 0.5;
/** Real seconds a burn plays out over at the rate its length buys, inside what
 *  the guidance can steer it at. */
export const BURN_WATCH_REAL_S = 6;
/** The same for a course fix, minutes long and not worth the departure's watch. */
export const BURN_WATCH_FIX_REAL_S = 2;

/** The rate Auto-warp aims at `toStepS` simulated seconds short of a step, `leadS`
 *  real seconds out, never under `floorRate`, the rate GO's own ladder asks for there. */
export function autoWarpTargetRate(toStepS: number, floorRate: number, leadS = AUTO_WARP_LEAD_REAL_S): number {
  return Math.max(floorRate, Math.max(0, toStepS) / leadS);
}

/** One frame of Auto-warp's ease from `rate` toward `target`: down at the clock's
 *  own ramp, up settling over AUTO_WARP_SETTLE_S at no more than
 *  AUTO_WARP_CLIMB_NEPERS_PER_S, and never so fast that a step `toStepS` out (null
 *  in a burn) comes inside AUTO_WARP_MIN_LEAD_REAL_S unless `floorRate`, GO's own
 *  ask there, is faster still. It never falls faster than the clock's own ramp. */
export function autoWarpEase(
  rate: number, target: number, realDtS: number, toStepS: number | null, floorRate: number,
): number {
  const dt = Math.max(0, realDtS);
  const climb = rate > 0 && rate < target
    ? Math.min(AUTO_WARP_CLIMB_NEPERS_PER_S, Math.log(target / rate) / AUTO_WARP_SETTLE_S) : 0;
  const eased = !(rate > 0) ? target
    : rate < target ? Math.min(target, rate * Math.exp(climb * dt))
      : Math.max(target, rate / Math.exp(WARP_RAMP_NEPERS_PER_S * dt));
  if (toStepS === null) return eased;
  const fall = rate > 0 ? rate / Math.exp(WARP_RAMP_NEPERS_PER_S * dt) : 0;
  return Math.min(eased, Math.max(floorRate, Math.max(0, toStepS) / AUTO_WARP_MIN_LEAD_REAL_S, fall));
}

/** Real seconds a burn's pace settles onto its watch rate over, climbing at the
 *  clock's own ramp: a burn's view is the ship, not a target closing at one zoom. */
export const BURN_CLIMB_SETTLE_S = 0.25;

/** One frame of a burn's watch pace from `rate` toward `target`, up and down at the clock's own ramp. */
export function burnWatchEase(rate: number, target: number, realDtS: number): number {
  const dt = Math.max(0, realDtS);
  if (!(rate > 0)) return target;
  if (rate >= target) return Math.max(target, rate / Math.exp(WARP_RAMP_NEPERS_PER_S * dt));
  const climb = Math.min(WARP_RAMP_NEPERS_PER_S, Math.log(target / rate) / BURN_CLIMB_SETTLE_S);
  return Math.min(target, rate * Math.exp(climb * dt));
}

/** A burn `burnS` simulated seconds long played out over `watchS`, never
 *  under the rate the guidance asks for nor over the `ceiling` it can steer at. */
export function burnWatchRate(burnS: number, rate: number, ceiling: number, watchS = BURN_WATCH_REAL_S): number {
  return Math.max(rate, Math.min(ceiling, burnS / watchS));
}

const AUTO_WARP_STORE_KEY = 'go-auto-warp';

/** Whether GO's Auto-warp is on as the player last left it: on unless switched off. */
export function loadAutoWarpOn(): boolean {
  try {
    return globalThis.localStorage?.getItem(AUTO_WARP_STORE_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function saveAutoWarpOn(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(AUTO_WARP_STORE_KEY, on ? 'on' : 'off');
  } catch {
    // Private mode or a full quota: the switch still works for this visit.
  }
}
